
import { defineComponent, onBeforeMount, onBeforeUnmount, reactive } from 'vue'

// components
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import List from '@/common/components/UI/List/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import OrdersWithoutAddressListItem from '@/modules/DispatcherModule/components/OrdersWithoutAddressListItem/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisation
import { dispatcher } from '@/core/realisations/dispatcher.realisation'
import { cities } from '@/core/realisations/cities.realisation'

// types
import { DispatcherOrderWithoutAddressT } from '@/core/types/dispatcher.types'
import { CityT } from '@/core/types/cities.types'

// composable
import useDispatcherMap from '@/modules/DispatcherModule/composable/useDispatcherMap'

// event bus
import emitter from 'tiny-emitter/instance'
import { DISPATCHER_EVENTS } from '@/core/enums/dispatcher.enums'

export default defineComponent({
    components: {
        'failed-fetch': FailedFetch,
        list: List,
        'the-select': TheSelect,
        'the-button': TheButton,
        'orders-without-address-list-item': OrdersWithoutAddressListItem,
    },
    setup() {
        const data_state = reactive({
            orders: new DataState<DispatcherOrderWithoutAddressT>({}),
            cities: new DataState<CityT>({}),
        })

        const filters_state = reactive({
            sender_city_id: null as number | null,
            is_setted: false,
        })

        const dispatcher_map = useDispatcherMap()

        const loadOrdersWithoutAddress = async (page: number) => {
            data_state.orders.pagination.current_page = page

            try {
                data_state.orders.is_loading = true

                const response = await dispatcher.getOrdersWithoutAddress({
                    page,
                    sender_city_id: filters_state.is_setted ? filters_state.sender_city_id : null,
                })

                data_state.orders.list = response.data
                data_state.orders.pagination.total_pages = response.meta.last_page

                data_state.orders.is_failed = false
                data_state.orders.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.orders.failure.catchError(error)

                if (!data_state.orders.failure.error_state.is_cancel) {
                    data_state.orders.is_failed = true
                }
            } finally {
                if (!data_state.orders.failure.error_state.is_cancel) {
                    data_state.orders.is_loading = false
                }
            }
        }

        const startSetAddress = (order: DispatcherOrderWithoutAddressT) => {
            dispatcher_map.startSetAddress(order)
        }

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCity = (search_query: string) => {
            // data_state.cities.pagination.current_page
            loadCities(1, search_query)
        }

        const unshiftFindedCity = async (city: CityT) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        onBeforeMount(() => {
            loadCities(1)
            loadOrdersWithoutAddress(1)

            emitter.on(DISPATCHER_EVENTS.DISPATCHER_SAVE_ADDRESS, () => {
                loadOrdersWithoutAddress(data_state.orders.pagination.current_page)
            })
        })

        onBeforeUnmount(() => {
            if (dispatcher_map.setting_address_state.is_setting) {
                dispatcher_map.cancelSetAddress()
            }

            emitter.off(DISPATCHER_EVENTS.DISPATCHER_SAVE_ADDRESS)
        })

        const resetFilters = () => {
            filters_state.sender_city_id = null
            filters_state.is_setted = false
            loadOrdersWithoutAddress(1)
        }

        const applyFilters = () => {
            filters_state.is_setted = true
            loadOrdersWithoutAddress(1)
        }

        return {
            data_state,
            filters_state,
            dispatcher_map,
            loadOrdersWithoutAddress,
            startSetAddress,
            loadCities,
            loadMoreCities,
            searchCity,
            unshiftFindedCity,
            resetFilters,
            applyFilters,
        }
    },
})
